import { useCallback, useEffect, useRef } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import useGetMySelf from "@/features/auth/use-get-my-self";
import useIdentity from "@/features/ui/hooks/use-identity";
import {
  channelTalkBootedState,
  channelTalkMessengerOpenState,
  channelTalkPendingOpenState,
  channelTalkUnreadState,
} from "@/shared/store/channelTalkStore";
import {
  clearChannelTalkMemberSession,
  rememberChannelTalkMemberSession,
  shutdownChannelTalk,
} from "@/shared/utils/channelTalk";
import { needsCompanySelection } from "@/features/ui/helpers/identity-helpers";

const PLUGIN_KEY = "9426f2f9-abe7-4066-b4b2-f68e0ab0398e";

// 익명 boot 을 memberId 대신 가리키는 값. undefined(아직 boot 안 함)와 구분하려고 쓴다.
const ANONYMOUS = null;

/**
 * 채널톡 boot 생명주기.
 *
 * 회사 타입과 관계없이 모두에게 띄운다. 로그인 전(랜딩·로그인·회원가입 등)과 가입 승인 대기 중,
 * 이 탭에서 아직 회사를 고르지 않았을 때는 익명으로, 그 외에는 지금 회사의 멤버로 띄운다.
 *
 * 로그인 전 화면에서도 떠야 하므로 모든 라우트가 지나는 AppLayout 에서 한 번 호출한다.
 * (예전에는 AppIdentitySelector 안에 있었는데, 모바일에서는 그 셀렉트가 GNB 드로어
 *  안에만 있고 Chakra Drawer 는 닫혀 있으면 children 을 마운트하지 않는다.
 *  그래서 햄버거 메뉴를 열기 전까지 boot 이 실행되지 않아 런처가 비활성으로 떠 있었다.)
 */
export default function useChannelTalkBoot() {
  const identity = useIdentity();
  const { data: user } = useGetMySelf();
  const setMessengerOpen = useSetRecoilState(channelTalkMessengerOpenState);
  const setUnread = useSetRecoilState(channelTalkUnreadState);
  const setBooted = useSetRecoilState(channelTalkBootedState);
  const isBooted = useRecoilValue(channelTalkBootedState);
  const [isPendingOpen, setPendingOpen] = useRecoilState(
    channelTalkPendingOpenState
  );
  // 지금 떠 있는 세션의 memberId. 익명이면 ANONYMOUS, boot 전이면 undefined.
  const bootedMemberIdRef = useRef<string | null | undefined>(undefined);

  const resetState = useCallback(() => {
    setBooted(false);
    setMessengerOpen(false);
    setUnread(0);
  }, [setBooted, setMessengerOpen, setUnread]);

  const boot = useCallback(
    (
      memberId: string | null,
      profile?: ChannelService.BootOption["profile"]
    ) => {
      // 익명 → 멤버(로그인), 회사 전환처럼 사람이 바뀌면 이전 세션을 먼저 끊는다.
      // 그대로 boot 하면 익명으로 나눈 대화가 로그인한 멤버 쪽에 섞여 보일 수 있다.
      if (
        bootedMemberIdRef.current !== undefined &&
        bootedMemberIdRef.current !== memberId
      ) {
        shutdownChannelTalk();
        resetState();
      }
      bootedMemberIdRef.current = memberId;

      // shutdown() 은 메모리만 비우고 채널톡 쿠키는 남긴다. 그대로 익명 boot 하면
      // 로그아웃 전이나 다른 탭의 멤버 세션 쿠키가 실려 익명이 아니라 그 멤버로 붙는다.
      if (memberId === ANONYMOUS) {
        clearChannelTalkMemberSession();
      } else {
        rememberChannelTalkMemberSession();
      }

      ChannelService.loadScript();
      ChannelService.boot(
        {
          pluginKey: PLUGIN_KEY,
          ...(memberId !== ANONYMOUS && { memberId, profile }),
          // 기본 플로팅 런처는 위치를 옮길 수 없어 쓰지 않는다.
          // 대신 ChannelTalkLauncher 가 같은 모양의 버튼을 직접 그린다.
          hideChannelButtonOnBoot: true,
          // 답변 도착 미리보기·마케팅 팝업도 띄우지 않는다.
          // 화면 위에 떠 있는 것은 자체 런처 하나로 통일하고, 새 메시지는 그 뱃지로 알린다.
          // (이 팝업이 뜨면 채널톡이 기본 런처 영역까지 함께 되살린다 — 숨김이 풀리는 경로이기도 하다)
          hidePopup: true,
        },
        (error) => {
          if (error) {
            setBooted(false);
            return;
          }
          // boot 은 로그인·회사 전환·유저 갱신마다 다시 실행되므로 그때마다 다시 숨긴다.
          ChannelService.hideChannelButton();
          // 채널톡은 상담원 답변·새 상담 같은 이벤트가 오면 숨겨둔 기본 버튼을 스스로 되살린다.
          // 이벤트가 올 때마다 다시 숨겨야 자체 런처만 남는다.
          ChannelService.onShowMessenger(() => {
            ChannelService.hideChannelButton();
            setMessengerOpen(true);
          });
          ChannelService.onHideMessenger(() => {
            ChannelService.hideChannelButton();
            setMessengerOpen(false);
          });
          // 기본 런처의 안 읽은 표시를 대신 그리기 위해 받아 둔다.
          ChannelService.onBadgeChanged((unread) => {
            ChannelService.hideChannelButton();
            setUnread(unread);
          });
          ChannelService.onChatCreated(() => {
            ChannelService.hideChannelButton();
          });
          ChannelService.onFollowUpChanged(() => {
            ChannelService.hideChannelButton();
          });
          // 여기부터 showMessenger/hideMessenger 가 실제로 먹는다.
          setBooted(true);
        }
      );
    },
    [resetState, setBooted, setMessengerOpen, setUnread]
  );

  useEffect(() => {
    // 로그인 전이거나 가입 승인 대기 중이면 누구인지 알 수 없으니 익명으로 띄운다.
    if (identity?.company == null) {
      boot(ANONYMOUS);
      return;
    }

    // 회사 선택은 탭마다 따로인 sessionStorage 에 있어서, 로그인한 채로 새 탭을 열면 비어 있다.
    // 그때 getIdentity() 는 scopes[0] 을 임시로 돌려주는데, 사용자가 고른 회사가 아니므로
    // 그 회사로 붙이면 안 된다. 익명으로 띄우고, 회사를 고르면(문서 리로드) 멤버로 바뀐다.
    if (needsCompanySelection()) {
      boot(ANONYMOUS);
      return;
    }
    const companyId = identity.company.id;

    // 멤버는 /users/me 응답이 와야 memberId·profile 을 채울 수 있다.
    // 그 사이에는 기존(익명) 세션을 그대로 둔다.
    if (!user) return;
    const userCompanyNames = user.companies
      .filter((c) => c.id === companyId)
      .map((c) => c.name)
      .join(", ");
    // memberId 형식은 BUYER 만 띄우던 때와 같다 — 기존 상담 이력이 그대로 이어진다.
    boot(`${user.id.toString()}_${companyId}`, {
      name: userCompanyNames,
      mobileNumber: user.mobileNumber,
      description: userCompanyNames,
    });
  }, [boot, identity, user]);

  // boot 전에 useOpenChannelTalk 으로 예약된 열기를 boot 이 끝나면 대신 처리한다.
  useEffect(() => {
    if (!isBooted || !isPendingOpen) return;
    setPendingOpen(false);
    ChannelService.showMessenger();
  }, [isBooted, isPendingOpen, setPendingOpen]);
}
